/**
 * Host-site registration is decided in code, not by the model.
 *
 * The prompt alone was not enough: the model kept answering a 掲載サイト request in
 * chat, and the follow-up URL then fell through applyExtractedUrlsToAgentAction and
 * became a crawl preview. A host origin is never a crawl target, so the routing is
 * enforced here and the model only writes the wording.
 */

const HOST_SITE_INTENT = [
  /掲載サイト/,
  /掲載先/,
  /設置(する)?サイト/,
  /埋め込み(先|用)?/,
  /埋め込む/,
  /ウィジェットを(設置|置)/,
  /設置スクリプト/,
  /トークンを(発行|取得)/,
  /\bhost site\b/i,
  /\bembed (site|code|script|token)\b/i,
  /\bissue (a )?token\b/i,
  /\bwidget snippet\b/i,
];

/** The admin is talking about where the widget is embedded, not about knowledge sources. */
export function isHostSiteIntent(text: string): boolean {
  if (typeof text !== 'string' || !text.trim()) return false;
  // Japanese is matched without spaces; English keeps them so \b still works.
  const collapsed = text.replace(/\s+/g, '');
  return HOST_SITE_INTENT.some((pattern) => pattern.test(collapsed) || pattern.test(text));
}

/** A message that is only a URL, i.e. the answer to "which site?" and nothing else. */
export function isBareUrlMessage(text: string, extractedUrls: string[]): boolean {
  if (!Array.isArray(extractedUrls) || extractedUrls.length !== 1) return false;
  if (typeof text !== 'string') return false;
  const withoutUrl = text.replace(/https?:\/\/\S+/gi, '').replace(/[\s。、.,]/g, '');
  return withoutUrl.length === 0;
}

/** Same check for callers that have no extracted-URL list, i.e. the client before sending. */
export function isBareUrlText(text: string): boolean {
  if (typeof text !== 'string') return false;
  return isBareUrlMessage(text, text.match(/https?:\/\/\S+/gi) ?? []);
}

/**
 * Decide whether this turn must open the host-site panel.
 *
 * `panelOpen` is the panel the admin currently has open on the home. When the
 * host-site panel is already open, a URL pasted into the chat answers that panel.
 */
export function shouldOpenHostSitePanel(params: {
  lastUserText: string;
  extractedUrls: string[];
  panelOpen?: string | null;
  ingestPath?: 'crawl' | 'pages' | null;
}): boolean {
  const { lastUserText, extractedUrls, panelOpen, ingestPath } = params;

  // An explicit ingest flow wins: the admin walked into the crawl wizard on purpose.
  if (ingestPath === 'crawl' || ingestPath === 'pages') return false;

  if (isHostSiteIntent(lastUserText)) return true;
  if (panelOpen === 'embedSecurity' && isBareUrlMessage(lastUserText, extractedUrls)) return true;
  return false;
}

/** First http(s) URL of the turn, used to prefill the panel. */
export function hostSiteUrlFromTurn(lastUserText: string, extractedUrls: string[]): string | undefined {
  const fromList = Array.isArray(extractedUrls)
    ? extractedUrls.find((u) => typeof u === 'string' && /^https?:\/\//i.test(u))
    : undefined;
  if (fromList) return fromList;
  const match = typeof lastUserText === 'string' ? lastUserText.match(/https?:\/\/\S+/i) : null;
  return match ? match[0] : undefined;
}
